/**
 * ThemeBadge component with built-in lazy loading of theme colours
 * Autonomous component that fetches theme enrichment data internally
 * Can be used in any React app without external colour service
 */

'use client';

import { X } from 'lucide-react';
import { useThemeColorEnrichment } from '../hooks';

export interface ThemeBadgeTheme {
  id: string;
  name: string;
  color?: string | null;
}

export interface ThemeBadgeProps {
  /** Theme data with id, name, and optional colour */
  theme: ThemeBadgeTheme;
  /** Variant style for the badge */
  variant?: 'default' | 'compact' | 'filter';
  /** Whether the badge is removable (shows X button) */
  removable?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: (themeId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ThemeBadge displays a single theme with coloured border
 * Automatically fetches theme colour if not provided
 *
 * @example
 * ```tsx
 * <ThemeBadge
 *   theme={{ id: '1', name: 'Rail Freight Forward', color: '#2B5F9E' }}
 *   variant="default"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Without colour - will be fetched automatically
 * <ThemeBadge
 *   theme={{ id: '1', name: 'Green Logistics' }}
 *   variant="filter"
 *   removable={true}
 *   onRemove={(id) => console.log('Remove theme:', id)}
 * />
 * ```
 */
export function ThemeBadge({
  theme,
  variant = 'default',
  removable = false,
  onRemove,
  className = '',
}: ThemeBadgeProps) {
  const hasProvidedColor = Boolean(theme.color);
  const hasProvidedName = Boolean(theme.name && theme.name.trim() !== '');
  const enrichedData = useThemeColorEnrichment(theme.id, hasProvidedColor, hasProvidedName);

  // Use provided values first, then enriched values
  const finalColor = theme.color || enrichedData.color;
  const finalName = theme.name || enrichedData.name || theme.id;

  const baseClasses = 'inline-flex items-center gap-1 rounded-full border';

  const variantClasses = {
    default: 'bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary',
    compact: 'bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary',
    filter: 'bg-background px-2 py-0.5 text-xs font-medium text-primary',
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(theme.id);
    }
  };

  return (
    <span
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={{
        borderColor: finalColor || 'currentColor',
      }}
    >
      {finalName}
      {removable && onRemove && (
        <span
          onClick={handleRemove}
          className="hover:bg-primary/20 rounded-full cursor-pointer inline-flex"
          role="button"
          aria-label={`Remove ${finalName}`}
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </span>
  );
}
